import { useState, type FormEvent } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input, Select } from "../ui/input";
import { formatDateTime } from "../../lib/format";
import {
  CRM_MEETING_KIND_LABELS,
  CRM_MEETING_STATUS_LABELS,
  dateTimeLocalToIso,
  toDateTimeLocalValue,
} from "../../lib/crm";
import { useToastStore } from "../../stores/toast";
import type { CrmMeeting, CrmMeetingKind, CrmMeetingStatus } from "@shared/types";

interface Draft {
  title: string;
  kind: CrmMeetingKind;
  startAt: string;
  endAt: string;
  location: string;
  attendees: string;
  outcome: string;
  assignedUserId: string;
}

const EMPTY: Draft = {
  title: "",
  kind: "visio",
  startAt: "",
  endAt: "",
  location: "",
  attendees: "",
  outcome: "",
  assignedUserId: "",
};

interface MeetingListProps {
  accountId: string;
  items: CrmMeeting[];
  users: Array<{ id: string; name: string }>;
  onChanged: () => void;
}

export function MeetingList({ accountId, items, users, onChanged }: MeetingListProps) {
  const push = useToastStore((s) => s.push);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const base = `/api/crm/accounts/${accountId}/meetings`;

  const reset = () => {
    setDraft(EMPTY);
    setEditingId(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const startAt = dateTimeLocalToIso(draft.startAt);
    if (!draft.title.trim() || saving) return;
    if (!startAt) {
      push("danger", "Date et heure de début requises");
      return;
    }
    setSaving(true);
    try {
      await api(editingId ? `${base}/${editingId}` : base, {
        method: editingId ? "PATCH" : "POST",
        body: JSON.stringify({
          title: draft.title.trim(),
          kind: draft.kind,
          startAt,
          endAt: dateTimeLocalToIso(draft.endAt),
          location: draft.location.trim() || null,
          attendees: draft.attendees.trim() || null,
          outcome: draft.outcome.trim() || null,
          assignedUserId: draft.assignedUserId || null,
        }),
      });
      push("success", editingId ? "Rendez-vous mis à jour" : "Rendez-vous planifié");
      reset();
      onChanged();
    } catch {
      push("danger", "Échec de l'enregistrement du rendez-vous");
    } finally {
      setSaving(false);
    }
  };

  const edit = (m: CrmMeeting) => {
    setEditingId(m.id);
    setDraft({
      title: m.title,
      kind: m.kind,
      startAt: toDateTimeLocalValue(m.startAt),
      endAt: toDateTimeLocalValue(m.endAt),
      location: m.location ?? "",
      attendees: m.attendees ?? "",
      outcome: m.outcome ?? "",
      assignedUserId: m.assignedUserId ?? "",
    });
  };

  const changeStatus = async (id: string, status: CrmMeetingStatus) => {
    try {
      await api(`${base}/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      onChanged();
    } catch {
      push("danger", "Échec du changement de statut");
    }
  };

  const remove = async (id: string) => {
    try {
      await api(`${base}/${id}`, { method: "DELETE" });
      if (editingId === id) reset();
      push("success", "Rendez-vous supprimé");
      onChanged();
    } catch {
      push("danger", "Échec de la suppression");
    }
  };

  return (
    <section className="crm-section">
      <div className="crm-section__head">
        <h2 className="crm-section__title">Rendez-vous</h2>
        <div className="crm-section__actions">
          <span className="crm-chip crm-chip--muted">{items.length}</span>
        </div>
      </div>

      <form className="crm-form crm-form--inline" onSubmit={submit}>
        <Input
          placeholder="Objet (ex : démo de l'agent)"
          aria-label="Objet du rendez-vous"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
        <Select
          aria-label="Format du rendez-vous"
          value={draft.kind}
          onChange={(e) => setDraft({ ...draft, kind: e.target.value as CrmMeetingKind })}
        >
          {Object.entries(CRM_MEETING_KIND_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Input
          type="datetime-local"
          aria-label="Début"
          title="Début"
          value={draft.startAt}
          onChange={(e) => setDraft({ ...draft, startAt: e.target.value })}
        />
        <Input
          type="datetime-local"
          aria-label="Fin"
          title="Fin"
          value={draft.endAt}
          onChange={(e) => setDraft({ ...draft, endAt: e.target.value })}
        />
        <Input
          placeholder="Lieu / lien"
          aria-label="Lieu ou lien de connexion"
          value={draft.location}
          onChange={(e) => setDraft({ ...draft, location: e.target.value })}
        />
        <Select
          aria-label="Responsable du rendez-vous"
          value={draft.assignedUserId}
          onChange={(e) => setDraft({ ...draft, assignedUserId: e.target.value })}
        >
          <option value="">Non assigné</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
        {editingId ? (
          <>
            <Input
              placeholder="Participants"
              aria-label="Participants"
              value={draft.attendees}
              onChange={(e) => setDraft({ ...draft, attendees: e.target.value })}
            />
            <Input
              placeholder="Compte rendu"
              aria-label="Compte rendu"
              value={draft.outcome}
              onChange={(e) => setDraft({ ...draft, outcome: e.target.value })}
            />
          </>
        ) : null}
        <div className="crm-form__actions">
          {editingId ? (
            <Button type="button" variant="ghost" size="sm" onClick={reset}>
              Annuler
            </Button>
          ) : null}
          <Button type="submit" size="sm" disabled={saving || !draft.title.trim()}>
            {editingId ? "Enregistrer" : "Planifier"}
          </Button>
        </div>
      </form>

      <table className="table crm-table">
        <thead>
          <tr>
            <th>Quand</th>
            <th>Objet</th>
            <th>Lieu / lien</th>
            <th>Responsable</th>
            <th>Statut</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={6} className="table__empty">
                Aucun rendez-vous — planifie le prochain échange ci-dessus.
              </td>
            </tr>
          ) : (
            items.map((m) => (
              <tr key={m.id}>
                <td data-label="Quand" title={m.endAt ? `Fin : ${formatDateTime(m.endAt)}` : undefined}>
                  {formatDateTime(m.startAt, { dateStyle: "medium", timeStyle: "short" })}
                </td>
                <td data-label="Objet" title={m.outcome ?? m.attendees ?? undefined}>
                  {m.title}{" "}
                  <span className="crm-chip crm-chip--muted">
                    {CRM_MEETING_KIND_LABELS[m.kind]}
                  </span>
                </td>
                <td data-label="Lieu / lien">{m.location ?? "—"}</td>
                <td data-label="Responsable">{users.find((u) => u.id === m.assignedUserId)?.name ?? "—"}</td>
                <td data-label="Statut">
                  <Select
                    aria-label={`Statut du rendez-vous ${m.title}`}
                    value={m.status}
                    onChange={(e) => void changeStatus(m.id, e.target.value as CrmMeetingStatus)}
                  >
                    {Object.entries(CRM_MEETING_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </td>
                <td className="table__actions">
                  <Button variant="ghost" size="sm" onClick={() => edit(m)}>
                    Modifier
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void remove(m.id)}>
                    Supprimer
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}
